import { useMemo } from 'react';

import PrecisionService from '@core/services/PrecisionService';
import { IAbstractRecord } from '@models';
import { getGenericLine, getLineCategory } from '@utils/helper-utils';

import type {
  GetProposalDetailsResponse,
  IBusinessPartnerPerson,
  IPolicyCoverForm,
  IPropertyRiskDetail,
  IProposalBeneficiaryPerson,
  IProposalExpatDetails,
  IProposalExpatInsuredDetails,
  IProposalInsuredPerson,
  IProposalMarineDetails,
  IProposalMarineHullDetails,
  IProposalMedicalDetails,
  IProposalMedicalInsured,
  IProposalMotorDetails,
  IProposalPersons,
  IProposalPolicyBankersBlanketBondDetail,
  IProposalPolicyPlanCover,
  IProposalPolicyProperty,
  IProposalPolicyRiskDescription,
  IProposalResponse,
  IProposalTravelDetails
} from '../types';

import { GenericLines, LineCategory } from 'src/enums';

export function useProposalInitials(
  pd: IProposalResponse | undefined,
  covers: IProposalPolicyPlanCover[] | undefined,
  persons: IBusinessPartnerPerson[],
  proposalDetailsData?: GetProposalDetailsResponse
) {
  const initialCovers: IPolicyCoverForm[] = useMemo(() => {
    return (covers ?? []).map((c) => ({
      policyPlanCoverId: c?.id,
      coverName: c?.planCover?.cover?.name,
      policyCover: c?.planCoverID,
      isMain: c?.isMain,
      isMandatory: c?.isMandatory,
      isEditable: c?.planCover?.isEditable,
      coverEffectiveFrom: c?.effectiveFrom,
      coverEffectiveTo: c?.effectiveTo,
      sumInsuredIsAdditive: c?.sumInsuredIsAdditive,
      coverSumInsured: c?.sumInsured,
      coverPremiumType: c?.premiumType,
      coverPremiumAmount: c?.premiumAmount,
      coverPremiumPercentage: PrecisionService.multiplyBy100(
        c?.premiumPercentage
      ),
      excessOnClaimType: c?.excessOnClaimType,
      excessOnClaimAmount: c?.excessOnClaimAmount,
      excessOnClaimPercentage: PrecisionService.multiplyBy100(
        c?.excessOnClaimPercentage
      ),
      excessOnClaimDays: c?.excessOnClaimDays
    }));
  }, [covers]);

  const initialMotorDetails: IProposalMotorDetails = useMemo(() => {
    const motor = proposalDetailsData?.production?.getProposalPolicyMotor?.[0];

    return {
      nameOnLicenseEN: motor?.nameOnLicenseEN,
      nameOnLicenseAR: motor?.nameOnLicenseAR,
      vignetteCode: motor?.vignetteCode,
      vignette: motor?.vignette,
      plateCode: motor?.vehicle?.plateCode?.id,
      plateNumber: motor?.vehicle?.plateNumber,
      usage: motor?.vehicle?.usageType?.id,
      body: motor?.vehicle?.bodyType?.id,
      engineType: motor?.vehicle?.engineType?.id,
      make: motor?.vehicle?.make?.id,
      model: motor?.vehicle?.model?.id,
      color: motor?.vehicle?.color?.id,
      yearOfMake: motor?.vehicle?.yearOfMake,
      engine: motor?.vehicle?.engine,
      weight: motor?.vehicle?.weight,
      engineSize: motor?.vehicle?.engineSize,
      seatingCapacity: motor?.vehicle?.seatingCapacity,
      chassis: motor?.vehicle?.chassis,
      horsePower: motor?.vehicle?.horsePower,
      carValue: motor?.vehicle?.carValue,
      agencyRepairYears: motor?.agencyRepairYears,
      depreciationYears: motor?.depreciationYears,
      acalCategory:
        proposalDetailsData?.production?.getProposalDetails?.plan
          ?.acalCategoryEntity?.id,
      sosService:
        proposalDetailsData?.production?.getProposalDetails?.plan?.sosService,
      scratching:
        proposalDetailsData?.production?.getProposalDetails?.plan?.scratching
    };
  }, [proposalDetailsData]);

  const initialMedicalDetails: IProposalMedicalDetails = useMemo(() => {
    const medicalDetails =
      proposalDetailsData?.production?.getProposalPolicyMedical?.[0];
    const covers: IAbstractRecord[] =
      proposalDetailsData?.production?.getProposalPolicyPlanCovers
        ?.map((item) => ({
          coverID: item.coverID,
          isMandatory: item.isMandatory
        }))
        .filter((item) => Boolean(item.coverID));

    return {
      class: medicalDetails?.medicalClassId,
      level: medicalDetails?.levelId,
      nssf: medicalDetails?.nssf,
      covers: covers.length ? covers : undefined
    };
  }, [proposalDetailsData]);

  const initialMedicalInsureds: IProposalMedicalInsured[] = useMemo(() => {
    const persons =
      proposalDetailsData?.production?.getProposalPolicyPersons ?? [];

    const insureds = persons
      .filter((person) => person?.typeList?.toLowerCase() === 'insured')
      .map((person) => ({
        insuredID: person.personID,
        addressID: person.personAddressID,
        isPrincipal: person.isPrincipal,
        relation: person.relation,
        cardNumber: person.cardNumber,
        passportNumber: person.person.passportNumber,
        continuity: person.continuity,
        tpaReference1: person.tpaReference1,
        tpaReference2: person.tpaReference2,
        grApplicable: person.grApplicable ? 'YES' : 'NO',
        exclusions: person.exclusions,
        remarks: person.remarks,
        dateOfBirth: person?.person?.dateOfBirth,
        personType: person?.person?.type
      }));

    if (insureds.length === 0) {
      return [
        {
          insuredID: '',
          addressID: '',
          isPrincipal: true,
          relation: 'PRINCIPAL',
          cardNumber: '',
          passportNumber: '',
          continuity: null,
          tpaReference1: '',
          tpaReference2: '',
          grApplicable: 'YES',
          exclusions: '',
          remarks: '',
          dateOfBirth: '',
          personType: 'INDIVIDUAL'
        }
      ];
    }

    return insureds;
  }, [proposalDetailsData]);

  const initialMarineDetails: IProposalMarineDetails = useMemo(() => {
    const marine =
      proposalDetailsData?.production?.getProposalPolicyMarine?.[0];

    return {
      matter: marine?.matter?.id,
      description: marine?.description,
      vesselName: marine?.vessalName,
      vesselAge: marine?.vessalAge,
      from: marine?.departureFrom,
      to: marine?.departureTo,
      via: marine?.transportationVia,
      survey: marine?.survey,
      cargoContract: marine?.cargoContract,
      bank: marine?.bank,
      sumPerShipmentCurrency:
        marine?.sumPerShipmentCurrency?.id ?? pd?.policyDetails?.currencyID,
      sumPerShipmentCurrencySymbol:
        (pd?.policyDetails?.currency as unknown as string) || '',
      sumPerShipment: marine?.sumPerShipment,
      sumPerShipmentRate: marine?.sumPerShipmentRate ?? 1,
      sumPerShipmentPolicyCurrency:
        (marine?.sumPerShipment ?? 0) * (marine?.sumPerShipmentRate ?? 0),
      lcNumber: marine?.lcNumber,
      lcIssueDate: marine?.lcIssueDate,
      claimsPayableBy: marine?.claimsPayableBy,
      loading: marine?.loading
        ? PrecisionService.multiplyBy100(marine?.loading)
        : '',
      remarks: marine?.remarks
    };
  }, [proposalDetailsData]);

  const initialTravelDetails: IProposalTravelDetails = useMemo(() => {
    const travelDetails =
      proposalDetailsData?.production?.getProposalPolicyTravel?.[0];
    const covers: IAbstractRecord[] =
      proposalDetailsData?.production?.getProposalPolicyPlanCovers
        ?.map((item) => ({
          coverID: item.coverID,
          isMandatory: item.isMandatory
        }))
        .filter((item) => Boolean(item.coverID));

    return {
      departureId: travelDetails?.departureId,
      destinationId: travelDetails?.destinationId,
      duration: travelDetails?.duration ?? '1',
      covers: covers.length ? covers : undefined
    };
  }, [proposalDetailsData]);

  const initialMarineHullDetails: IProposalMarineHullDetails = useMemo(() => {
    const marineHull =
      proposalDetailsData?.production?.getProposalPolicyMarineHull?.[0];

    return {
      vesselName: marineHull?.vesselName,
      connectionPort: marineHull?.connectionPort,
      territorialWaters: marineHull?.territorialWaters,
      usage: marineHull?.usage,
      builder: marineHull?.builder,
      constructionYear: marineHull?.constructionYear,
      registeryPort: marineHull?.registryPort,
      length: marineHull?.length,
      weight: marineHull?.weight,
      height: marineHull?.height,
      engineType: marineHull?.engineType,
      engineNumber: marineHull?.engineNumber,
      engineYearBuilt: marineHull?.engineYearBuilt,
      engineHorsePower: marineHull?.engineHorsePower,
      engineMaxDesignedSpeed: marineHull?.engineMaxDesignedSpeed,
      engineFuel: marineHull?.engineFuel,
      remarks: marineHull?.remarks
    };
  }, [proposalDetailsData]);

  const initialBankersDetails: IProposalPolicyBankersBlanketBondDetail[] =
    useMemo(() => {
      return proposalDetailsData?.production
        ?.getProposalPolicyBankersBlanketBondsDetails.length
        ? proposalDetailsData?.production
            ?.getProposalPolicyBankersBlanketBondsDetails
        : [
            {
              branchName: '',
              isMain: false,
              isAgency: false,
              employeeNumber: undefined,
              underwritingYear: undefined,
              infedility: undefined,
              burglary: undefined,
              holdup: undefined,
              inSafe: undefined,
              inTransit: undefined,
              forgedCheques: undefined,
              counterfeitCurrencies: undefined,
              clause6: undefined,
              falsification: undefined
            }
          ];
    }, [proposalDetailsData]);

  const initialPropertyRiskDetails: IPropertyRiskDetail[] = useMemo(() => {
    const propertyArray =
      proposalDetailsData?.production?.getProposalPolicyProperty ?? [];

    const sorted = [...propertyArray].sort(
      (a: IProposalPolicyProperty, b: IProposalPolicyProperty) => {
        const ao = Number(a?.order);
        const bo = Number(b?.order);

        return ao - bo;
      }
    );

    return sorted.length
      ? sorted.map((property, idx) => ({
          riskDescription: property?.riskDescription ?? '',
          geoLocation: property?.geoLocationID ?? '',
          location: property?.location ?? '',
          planCovers: property?.planCoverPropertyDetails ?? [],
          riskOrder: idx + 1
        }))
      : [
          {
            riskDescription: '',
            geoLocation: '',
            location: '',
            planCovers: [
              {
                planCoverID: '',
                premium: null,
                riskRate: null,
                sumInsured: null
              }
            ],
            riskOrder: 1
          }
        ];
  }, [proposalDetailsData]);

  const initialDescriptionOfRiskDetails = useMemo(() => {
    const descriptionOfRisk =
      proposalDetailsData?.production?.getProposalPolicyRiskDescription ?? [];

    const sorted = [...descriptionOfRisk].sort(
      (
        a: IProposalPolicyRiskDescription,
        b: IProposalPolicyRiskDescription
      ) => {
        const ao = Number(a?.order ?? 0);
        const bo = Number(b?.order ?? 0);

        return ao - bo;
      }
    );

    const mapped = sorted.map((item, idx) => ({
      descriptionOfRisk: item.riskDescription ?? '',
      geoLocation: item.geoLocationID ?? '',
      riskOrder: idx + 1
    }));

    return mapped.length
      ? mapped
      : [{ descriptionOfRisk: '', geoLocation: '', riskOrder: 1 }];
  }, [proposalDetailsData]);

  const personsWithAddress = pd?.persons?.map((p: any) => ({
    ...p,
    address: p.addressID
  }));

  const initialBeneficiaries: IProposalBeneficiaryPerson[] = useMemo(() => {
    const filtered =
      personsWithAddress
        ?.filter((p) => String(p?.type ?? '').toLowerCase() === 'beneficiary')
        ?.map((person, index) => {
          const pAny: any = person;
          const percentage =
            pAny?.beneficiaryPercentage === null ||
            pAny?.beneficiaryPercentage === undefined
              ? 0
              : pAny.beneficiaryPercentage;

          const address: string = pAny?.address ?? pAny?.personAddressID ?? '';

          return {
            id: pAny?.id || '',
            name: pAny?.name || '',
            address,
            beneficiaryPercentage: PrecisionService.multiplyBy100(percentage),
            order: index + 1
          } as IProposalBeneficiaryPerson;
        }) || [];

    return filtered;
  }, [pd?.persons]);

  const expatDetails = useMemo<Partial<IProposalExpatDetails>>(
    () => ({
      medicalClass:
        proposalDetailsData?.production?.getProposalPolicyExpat[0]
          ?.medicalClassID || null,
      emergency:
        proposalDetailsData?.production?.getProposalPolicyExpat[0]?.emergency ||
        null,
      continuity:
        typeof proposalDetailsData?.production?.getProposalPolicyExpat[0]
          ?.continuity === 'boolean'
          ? proposalDetailsData?.production?.getProposalPolicyExpat[0]
              .continuity
            ? 'YES'
            : 'NO'
          : (proposalDetailsData?.production?.getProposalPolicyExpat[0]
              ?.continuity as any) || null,
      continuityNumber:
        proposalDetailsData?.production?.getProposalPolicyExpat[0]
          ?.continuityNumber || null,
      exclusion:
        proposalDetailsData?.production?.getProposalPolicyExpat[0]?.exclusion ||
        null,
      remarks:
        proposalDetailsData?.production?.getProposalPolicyExpat[0]?.remarks ||
        null
    }),
    [proposalDetailsData]
  );

  const initialValues = useMemo(() => {
    const filtered =
      pd?.persons
        ?.filter((p) => p?.type === 'INSURED')
        ?.map((person, index) => {
          const pAny = person;

          return {
            address: pAny.addressID,
            id: pAny.id,
            limitOfCover: pAny.limitCover,
            name: pAny.name,
            order: index + 1
          } as IProposalInsuredPerson;
        }) || [];

    const insured: IProposalInsuredPerson[] =
      filtered.length > 0
        ? filtered
        : [
            {
              address: '',
              id: '',
              limitOfCover: 0,
              name: '',
              order: 1
            }
          ];

    const expatInsuredList: IProposalExpatInsuredDetails[] =
      (pd?.persons
        ?.filter((p) => p?.type === 'INSURED')
        ?.map((pAny) => {
          const matched = persons?.find((person) => person.id === pAny.id);

          if (matched) {
            return {
              gender: matched.gender,
              dateOfBirth: matched.dateOfBirth,
              nationality: matched.nationalityId,
              occupationClass: matched.occupationClass,
              profession: matched.profession,
              passportNumber: matched.passportNumber
            } as IProposalExpatInsuredDetails;
          }

          return undefined;
        })
        .filter(Boolean) as IProposalExpatInsuredDetails[]) ?? [];

    const expatInsuredDetails: IProposalExpatInsuredDetails =
      expatInsuredList[0] ?? {
        gender: null,
        dateOfBirth: '',
        nationality: null,
        occupationClass: null,
        profession: '',
        passportNumber: ''
      };

    const personsWithAddress = pd?.persons?.map((p: any) => ({
      ...p,
      address: p.addressID
    }));

    const payerOwner: IProposalPersons & { ownerIsDifferent?: boolean } = {
      ownerIsDifferent:
        (personsWithAddress?.find((p) => p.type === 'OWNER') &&
          personsWithAddress?.find((p) => p.type === 'OWNER')?.id !==
            personsWithAddress?.find((p) => p.type === 'PAYER')?.id) ||
        false,
      payer: personsWithAddress?.find((p) => p.type === 'PAYER'),
      owner: personsWithAddress?.find((p) => p.type === 'OWNER'),
      introducedBy: personsWithAddress?.find((p) => p.type === 'INTRODUCED_BY')
    };

    // Calculate default PolicyEndDate based on line code
    const calculateDefaultPolicyEndDate = () => {
      const lineCode =
        proposalDetailsData?.production?.getProposalDetails?.line?.code?.toString();
      const isTravel = getLineCategory(lineCode) === LineCategory.Travel;
      const isMarine = getGenericLine(lineCode) === GenericLines.MARINE;
      const effectiveDate =
        proposalDetailsData?.production?.getProposalDetails
          ?.policyEffectiveDate;

      if (!effectiveDate) return undefined;

      if (isMarine) {
        return null;
      } else if (!isTravel) {
        const date = new Date(effectiveDate);
        date.setFullYear(date.getFullYear() + 1);

        return date.toISOString();
      } else {
        return proposalDetailsData?.production?.getProposalDetails
          ?.policyExpiryDate;
      }
    };

    return {
      covers: initialCovers,
      policyCurrency:
        proposalDetailsData?.production?.getProposalDetails?.policyCurrency,
      chargesAndCost: {
        policyCost: pd?.quote?.policyCost ?? 0,
        chargesPercentage: PrecisionService.multiplyBy100(
          pd?.quote?.chargesPercentage
        ),
        TPAFeesAmount: pd?.quote?.tpaFees ?? 0,
        TPAFeesType: pd?.quote?.tpaFeesType ?? '',
        discount: pd?.quote?.initialDiscount
      },
      offerDetails: {
        status: pd?.offerDetails?.status ?? '',
        details: pd?.offerDetails?.details ?? {},
        covers: pd?.offerDetails?.covers ?? [],
        breakDown: pd?.offerDetails?.breakDown ?? {},
        coverage: pd?.offerDetails?.coverage ?? '',
        totalPremium: pd?.offerDetails?.totalPremium ?? 0,
        maxInsured: pd?.offerDetails?.maxInsured ?? 0,
        statusReason: pd?.offerDetails?.statusReason ?? '',
        createdOn: pd?.offerDetails?.createdOn ?? ''
      },
      insuredPerson: insured,
      payerOwner,
      marineDetails: initialMarineDetails,
      marineHullDetails: initialMarineHullDetails,
      bankersDetails: initialBankersDetails,
      propertyRiskDetails: initialPropertyRiskDetails,
      descriptionOfRiskDetails: initialDescriptionOfRiskDetails,
      beneficiaries: initialBeneficiaries,
      expatDetails: expatDetails,
      expatInsuredDetails: expatInsuredDetails,
      motorDetails: initialMotorDetails,
      medicalDetails: initialMedicalDetails,
      medicalInsureds: initialMedicalInsureds,
      travelDetails: initialTravelDetails,
      documents: pd.documents ?? undefined,
      policyEndDate: calculateDefaultPolicyEndDate(),
      policyEffectiveDate:
        proposalDetailsData?.production?.getProposalDetails
          ?.policyEffectiveDate,
      policyIssueDate:
        proposalDetailsData?.production?.getProposalDetails?.policyIssueDate,
      policyReinsuranceSubstandard:
        proposalDetailsData?.production?.getProposalDetails
          ?.policyReinsuranceSubstandard ?? 'NO',
      policyReinsuranceSubstandardLimit:
        proposalDetailsData?.production?.getProposalDetails
          ?.policyReinsuranceSubstandardLimit ?? ''
    };
  }, [
    pd,
    initialCovers,
    initialMarineDetails,
    initialMarineHullDetails,
    initialBankersDetails,
    initialPropertyRiskDetails,
    initialDescriptionOfRiskDetails,
    initialBeneficiaries,
    persons,
    proposalDetailsData
  ]);

  return {
    initialValues
  };
}
